import { useCallback, useMemo, useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { useRefreshWorkspaceMembers } from '@cbhq/identity-api-hooks/account-management/fetch';

import { useStepUpSubmission } from '../../../hooks/useStepUpSubmission';
import { SaveFooter } from '../components/SaveFooter';

import type { UseEditSubmissionParams } from './useEditSubmission';
import { useEditSubmission } from './useEditSubmission';

type EditMemberSaveMessages = {
  saveButton: MessageDescriptor;
  saveSuccess: MessageDescriptor;
  saveError: MessageDescriptor;
};

type UseEditMemberSaveParams = Omit<
  UseEditSubmissionParams,
  'isFormValid' | 'onSuccess' | 'onFailure'
> & {
  /** Whether the selection differs from the member's roles. Shows the save footer and allows saving. */
  hasChanges: boolean;
  isGatedRoleMutation: boolean;
  /** Hides the save footer when the current user may not edit the member. */
  isEditable: boolean;
  messages: EditMemberSaveMessages;
  onClose: () => void;
};

/**
 * Saving a member's roles from `MultiLEEditTray` or `SingleLEEditTray`: the
 * step-up challenge, the success and error toasts, and the save footer. A save
 * succeeds by refreshing the members and closing the tray. Saving stays blocked
 * while "Revoke admin access" is in flight (`setIsCancellingPendingRole`), since it
 * would resubmit the stale selection and re-grant the role.
 */
export function useEditMemberSave({
  hasChanges,
  isGatedRoleMutation,
  isEditable,
  messages,
  onClose,
  ...submission
}: UseEditMemberSaveParams) {
  const { formatMessage } = useIntl();
  const toast = useToast();
  const refreshWorkspaceMembers = useRefreshWorkspaceMembers({
    organizationId: submission.organizationId,
  });
  const [isCancellingPendingRole, setIsCancellingPendingRole] = useState(false);

  const { submit, stepUpModalProps, resetStepUpRecovery, handleStepUpProofTokenError } =
    useStepUpSubmission({ isGatedRoleMutation });

  const handleSaveSuccess = useCallback(() => {
    resetStepUpRecovery();
    toast.show(formatMessage(messages.saveSuccess));
    void refreshWorkspaceMembers();
    onClose();
  }, [
    formatMessage,
    messages.saveSuccess,
    onClose,
    refreshWorkspaceMembers,
    resetStepUpRecovery,
    toast,
  ]);

  const handleSaveFailure = useCallback(
    (error: unknown) => {
      if (handleStepUpProofTokenError(error)) {
        return;
      }
      toast.show(formatMessage(messages.saveError));
    },
    [formatMessage, handleStepUpProofTokenError, messages.saveError, toast],
  );

  const { isSaving, saveEdit } = useEditSubmission({
    ...submission,
    isFormValid: hasChanges,
    onSuccess: handleSaveSuccess,
    onFailure: handleSaveFailure,
  });

  const handleSave = useCallback(() => {
    if (isCancellingPendingRole) return;
    submit(saveEdit);
  }, [isCancellingPendingRole, saveEdit, submit]);

  const footer = useMemo(
    () =>
      isEditable ? (
        <SaveFooter
          visible={hasChanges || isSaving}
          onSave={handleSave}
          loading={isSaving || isCancellingPendingRole}
        >
          {formatMessage(messages.saveButton)}
        </SaveFooter>
      ) : null,
    [
      formatMessage,
      handleSave,
      hasChanges,
      isCancellingPendingRole,
      isEditable,
      isSaving,
      messages.saveButton,
    ],
  );

  return {
    footer,
    isSaving,
    stepUpModalProps,
    refreshWorkspaceMembers,
    setIsCancellingPendingRole,
  };
}
